import type { ReportRow } from '@/api/keywordMetrics';
import { keywordReportLookupKey } from '@/api/reportAdProducts';
import { canonicalAutoGroupId } from '@/utils/workspaceMatchTypeMetrics';
import {
  deriveMetrics,
  EMPTY_DERIVED_METRICS,
  EMPTY_RAW_METRICS,
  keywordNaturalLookupKey,
  rowToRaw,
  sumRawMetrics,
  type DerivedMetricTotals,
  type KeywordCompareRow,
} from '@/utils/workspaceAdProductMetrics';

export type { KeywordCompareRow };

/**
 * Previous-period metrics for a workspace keywords-table row.
 * Auto rows sum previous targets for the same match type and campaign.
 * Keyword rows try `adProduct::keywordId`, then campaign + text + match.
 */
export function previousMetricsForKeywordRow(
  row: KeywordCompareRow,
  previousKeywordLookup: Map<string, ReportRow>,
  previousTargetRows: ReportRow[]
): DerivedMetricTotals {
  if (row.rowKind === 'auto_target') {
    let raw = { ...EMPTY_RAW_METRICS };
    let found = false;
    const rowCampaign = String(row.campaignId ?? '').trim();
    const rowAdGroup = String(row.adGroupId ?? '').trim();
    for (const targetRow of previousTargetRows) {
      if (canonicalAutoGroupId(targetRow.matchType, targetRow.targetingText) !== row.matchType) {
        continue;
      }
      const targetCampaign = String(targetRow.campaignId ?? '').trim();
      if (rowCampaign && targetCampaign !== rowCampaign) continue;
      const targetAdGroup = String(targetRow.adGroupId ?? '').trim();
      if (rowAdGroup && targetAdGroup && targetAdGroup !== rowAdGroup) continue;
      raw = sumRawMetrics(raw, rowToRaw(targetRow));
      found = true;
    }
    return found ? deriveMetrics(raw) : EMPTY_DERIVED_METRICS;
  }

  const byId = previousKeywordLookup.get(keywordReportLookupKey(row.adProduct, row.keywordId));
  if (byId) return deriveMetrics(rowToRaw(byId));

  const naturalKey = keywordNaturalLookupKey(row.campaignId, row.keywordText, row.matchType);
  const byNaturalKey = naturalKey ? previousKeywordLookup.get(naturalKey) : undefined;
  return byNaturalKey ? deriveMetrics(rowToRaw(byNaturalKey)) : EMPTY_DERIVED_METRICS;
}
